/**
 * Entity Type Detection Utilities
 *
 * Determines the type of entity for routing to specialized components
 */

type EntityLike = {
  entity_type?: string;
  type?: string;
  routing_class?: string;
  category?: string;
  [key: string]: unknown;
};

/**
 * Check if entity is a Place/Venue type
 */
export const isPhysicalVenue = (entity: EntityLike | null | undefined): boolean => {
  if (!entity) return false;

  const candidates = [entity.entity_type, entity.type, entity.routing_class]
    .filter(Boolean)
    .map(v => String(v).toLowerCase());

  return candidates.some(
    value => value === "physicalvenue" || value === "location" || value === "venue" || value === "place",
  );
};

/**
 * Check if entity is an Event type
 */
export const isEventEntity = (entity: EntityLike | null | undefined): boolean => {
  if (!entity) return false;

  // Must have calendar data enabled to be treated as an event
  if (!(entity as any).calendar_data?.calendar_enabled) return false;

  const routingClass = String(entity.routing_class || "").toLowerCase();
  return routingClass.includes("event") || routingClass === "festival";
};

/**
 * Get normalized entity type for routing
 */
export const getEntityType = (entity: EntityLike | null | undefined): "venue" | "event" | "generic" => {
  if (isPhysicalVenue(entity)) return "venue";
  if (isEventEntity(entity)) return "event";
  return "generic";
};

/**
 * Check if entity has Google Places data
 */
export const hasGooglePlacesData = (entity: EntityLike | null | undefined): boolean => {
  if (!entity) return false;

  // Check direct google_places field
  if (entity.google_places && typeof entity.google_places === "object") {
    return true;
  }

  // Check nested in entity_data
  if (entity.entity_data && typeof entity.entity_data === "object") {
    const entityData = entity.entity_data as Record<string, unknown>;
    if (entityData.google_places && typeof entityData.google_places === "object") {
      return true;
    }
    if (entityData.primary_type || entityData.display_name) {
      return true;
    }
  }

  // Check top-level google places fields
  if (entity.primary_type || entity.display_name) {
    return true;
  }

  return false;
};
